"use client";

import { memo, useState, useCallback } from "react";
import { DynamicFormField } from "./DynamicFormField";

type FormFieldDef = {
  id: string;
  type: string;
  label: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: string[];
  validation?: {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    allowedFileTypes?: string[];
    maxFileSizeMB?: number;
  };
  sortOrder: number;
};

type FormFieldValue = string | number | boolean | string[] | File | null | undefined;

interface DynamicFormRendererProps {
  fields: FormFieldDef[];
  onSubmit: (responses: Record<string, FormFieldValue>) => Promise<void>;
  submitLabel?: string;
  onSkip?: () => void;
  skipLabel?: string;
  disabled?: boolean;
  initialValues?: Record<string, FormFieldValue>;
}

export const DynamicFormRenderer = memo(function DynamicFormRenderer({
  fields,
  onSubmit,
  submitLabel = "Submit",
  onSkip,
  skipLabel = "Skip for now",
  disabled = false,
  initialValues,
}: DynamicFormRendererProps) {
  const [values, setValues] = useState<Record<string, FormFieldValue>>(
    initialValues ?? {}
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const sortedFields = [...fields].sort((a, b) => a.sortOrder - b.sortOrder);

  const handleChange = useCallback((fieldId: string, value: FormFieldValue) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    setErrors((prev) => {
      if (prev[fieldId]) {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      }
      return prev;
    });
  }, []);

  const validate = useCallback(() => {
    const newErrors: Record<string, string> = {};
    for (const field of sortedFields) {
      const val = values[field.id];
      if (field.required) {
        if (
          val === undefined ||
          val === null ||
          val === "" ||
          (Array.isArray(val) && val.length === 0)
        ) {
          newErrors[field.id] = `${field.label} is required`;
        }
      }
      if (val && field.validation) {
        if (
          field.validation.minLength &&
          typeof val === "string" &&
          val.length < field.validation.minLength
        ) {
          newErrors[field.id] = `Minimum ${field.validation.minLength} characters`;
        }
        if (
          field.validation.maxLength &&
          typeof val === "string" &&
          val.length > field.validation.maxLength
        ) {
          newErrors[field.id] = `Maximum ${field.validation.maxLength} characters`;
        }
        if (field.validation.min != null && typeof val === "number" && val < field.validation.min) {
          newErrors[field.id] = `Minimum value is ${field.validation.min}`;
        }
        if (field.validation.max != null && typeof val === "number" && val > field.validation.max) {
          newErrors[field.id] = `Maximum value is ${field.validation.max}`;
        }
      }
      // Email format validation
      if (val && typeof val === "string" && field.type === "EMAIL" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        newErrors[field.id] = "Please enter a valid email address";
      }
      // Number type coercion check
      if (val !== undefined && val !== null && val !== "" && field.type === "NUMBER" && isNaN(Number(val))) {
        newErrors[field.id] = "Please enter a valid number";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [sortedFields, values]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;
      setSubmitting(true);
      try {
        await onSubmit(values);
      } finally {
        setSubmitting(false);
      }
    },
    [validate, onSubmit, values]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {sortedFields.map((field) => (
        <DynamicFormField
          key={field.id}
          field={field}
          value={values[field.id]}
          onChange={(val) => handleChange(field.id, val)}
          error={errors[field.id]}
        />
      ))}

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={disabled || submitting}
          className="rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-black hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "Submitting..." : submitLabel}
        </button>
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-white/50 hover:text-white/70 transition-colors"
          >
            {skipLabel}
          </button>
        )}
      </div>
    </form>
  );
});
